import type { SelectItem } from 'components/input/fields/SelectField'

// VERA-1095 (scope revision, mid-build): country, product-category/industry,
// and employee-count were ALL dropped from Card 2 (company details). Country
// has no downstream consumer for a company (every real reader is
// users.country / owner country / shipping country / geo-IP, and Stripe
// collects a billing country at checkout anyway); industry is enrichable
// from the email domain post-signup (filed as VRG-371 —
// Platform.Signup.Industry.Enrichment); employee count is replaced outright
// by the question below, which is a strictly better proxy for the same
// underlying thing it was trying to estimate.
//
// "About how many units do you produce a year?" is what's left — it feeds
// `pickTierForVolume` in SignUpPage.tsx, which preselects a plan from it. See
// that function's own doc comment for the full mapping. It's a strictly
// better signal than headcount ever was, AND it's the one qualifying fact
// that ISN'T publicly enrichable the way industry/company-size are.
//
// Ranges, not a free-text number — a self-serve visitor rarely knows their
// exact annual unit count, and picking a range is faster than typing a guess.
//
// The boundaries are VRG-305's ACQUISITION TIERS, which are the company's
// canonical way of sizing a brand (demos.ts ~:69-76):
//
//   Tier 1  1,000,000 units/yr      Tier 4    5,000
//   Tier 2    100,000               Tier 5      500
//   Tier 3     10,000
//
// An earlier version sized these from the plans' INCLUDED ACTIVATIONS instead
// (3/4/12/40 a month, i.e. 36-480 a year) and was wrong by three orders of
// magnitude at the top: its largest option was "more than 500 a year", which
// is Tier 5 — the SMALLEST tier — so the entire real customer range from 500
// to 1,000,000 collapsed into one bucket.
//
// Included activations are not a proxy for production volume. They are a base
// allotment that covers the subscription cost ("Pays for itself"); everything
// beyond is billed on the graduated $25 -> $5 activation scale. So volume maps
// to a plan through the TIER, never through the allotment.
//
// `tier` is what `pickTierForVolume` resolves against the live catalog.
//
// ─────────────────────────────────────────────────────────────────────────────
// 2026-08-24 — TWO DELIBERATE CHANGES, both approved by Spencer, one of which
// knowingly diverges from the acquisition tiers described above. Read before
// "correcting" either back.
//
// (1) BUSINESS WIDENED FROM 10,000 TO 25,000 — this is the divergence.
//
// 10,000 is acquisition Tier 3's boundary; 25,000 is not a tier boundary at
// all. The change was made on PRICE-STEP grounds: Business -> Enterprise is a
// 3.3x jump, and hanging it off a band only 5,000 units wide pushed brands into
// Enterprise on a rounding error. Widening gives the step somewhere to land.
//
// The cost is real and is NOT resolved here: a company sized by signup and the
// same company sized by sales (demos.ts) can now land in different tiers, so
// tier-based reporting across the two sources is no longer directly comparable.
// The fix is to reconcile the acquisition tiers and the graduated activation
// pricing ($25 -> $5) against these bands, which Spencer deferred until the
// self-serve signup flow is finished. Until that happens, treat a signup-sourced
// tier and a sales-sourced tier as different measurements of the same thing.
//
// (2) THE TOP TWO BUCKETS NO LONGER SELF-CHECKOUT.
//
// All three top buckets still resolve to the Enterprise PLAN — that is
// intentional, not laziness. The granularity is retained because a 1.2M-unit
// brand and a 30k-unit brand are the same plan but completely different sales
// motions, and that distinction is worth real money as lead data even though it
// selects the same product.
//
// What changed is what happens next. A million-unit brand putting itself on a
// $999/mo card payment both underprices the account and skips the conversation
// that account requires, so `checkout` carries that decision to step 3 rather
// than every consumer re-deriving it from the label.
//
// NOTE: `checkout` is DATA ONLY as of this commit. Step 3 does not yet act on
// it — wiring 'sales-assisted' and 'custom' to something other than a Stripe
// Checkout button is still to do, and until it lands the top buckets behave
// exactly like self-serve.
export type SignupCheckoutPath = 'self-serve' | 'sales-assisted' | 'custom'

export const PRODUCTION_VOLUME_OPTIONS: (SelectItem & {
  tier: string
  checkout: SignupCheckoutPath
})[] = [
  { value: 'under-500', label: 'Fewer than 500 units a year', tier: 'starter', checkout: 'self-serve' },
  { value: '500-5k', label: '500 – 5,000 units a year', tier: 'individual', checkout: 'self-serve' },
  { value: '5k-25k', label: '5,000 – 25,000 units a year', tier: 'business', checkout: 'self-serve' },
  { value: '25k-100k', label: '25,000 – 100,000 units a year', tier: 'enterprise', checkout: 'self-serve' },
  { value: '100k-1m', label: '100,000 – 1,000,000 units a year', tier: 'enterprise', checkout: 'sales-assisted' },
  { value: 'over-1m', label: 'More than 1,000,000 units a year', tier: 'enterprise', checkout: 'custom' },
]
